import React, { useEffect, useState } from 'react';
import type { QuoteData } from './QuoteGenerator';

const PrintableQuote: React.FC = () => {
  const [quoteData, setQuoteData] = useState<QuoteData | null>(null);
  const [mode, setMode] = useState<'client' | 'internal'>('client');

  useEffect(() => {
    const storedData = localStorage.getItem('quoteData');
    const storedMode = localStorage.getItem('quoteMode') as 'client' | 'internal';

    if (storedData) {
      setQuoteData(JSON.parse(storedData));
    }
    if (storedMode) {
      setMode(storedMode);
    }

    // Auto-print after a brief delay
    const timer = setTimeout(() => {
      window.print();
    }, 500);

    return () => clearTimeout(timer);
  }, []);

  if (!quoteData) {
    return (
      <div className="min-h-screen bg-[#0a0e27] flex items-center justify-center">
        <p className="text-white">Loading quote data...</p>
      </div>
    );
  }

  const {
    eventDetails,
    performers,
    addOns,
    disclaimer,
    sameTravel,
    globalTravel,
    sameRate,
    globalRate,
    sameHours,
    globalHours,
  } = quoteData;

  const isInternal = mode === 'internal';
  const performerCount = performers.length;

  const getPerformerRate = (performer: typeof performers[0]) => sameRate ? globalRate : performer.hourlyRate;
  const getPerformerTravel = (performer: typeof performers[0]) => sameTravel ? globalTravel : performer.travel;
  const getPerformerHours = (performer: typeof performers[0]) => sameHours ? globalHours : performer.hours;

  const calculatePerformerTotal = (performer: typeof performers[0]) => {
    const rate = getPerformerRate(performer);
    const travel = getPerformerTravel(performer);
    const hours = getPerformerHours(performer);
    return (hours * rate) + travel;
  };

  const totalServiceAmount = performers.reduce((sum, p) => {
    const rate = getPerformerRate(p);
    const hours = getPerformerHours(p);
    return sum + (hours * rate);
  }, 0);

  const totalTravelAmount = sameTravel
    ? performerCount * globalTravel
    : performers.reduce((sum, p) => sum + p.travel, 0);

  const grandTotal = totalServiceAmount + totalTravelAmount;
  const remainingAfterDeposit = grandTotal - eventDetails.deposit;

  const calculatePerformerCash = (performer: typeof performers[0]) => {
    if (grandTotal === 0) return 0;
    const performerTotal = calculatePerformerTotal(performer);
    const performerShare = performerTotal / grandTotal;
    return Math.round(remainingAfterDeposit * performerShare);
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr + 'T00:00:00');
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${days[date.getDay()]} ${date.getDate()} ${months[date.getMonth()]}`;
  };

  const formatTime = (timeStr: string) => {
    const [h, m] = timeStr.split(':').map(Number);
    const period = h >= 12 ? 'pm' : 'am';
    const hour = h > 12 ? h - 12 : h === 0 ? 12 : h;
    return `${hour}:${m.toString().padStart(2, '0')}${period}`;
  };

  return (
    <>
      <style>
        {`
          @media print {
            body {
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
              background: white !important;
            }
            .no-print {
              display: none !important;
            }
            .print-content {
              background: white !important;
              color: #1e293b !important;
            }
          }
        `}
      </style>
      {/* Screen view with dark theme */}
      <div className="min-h-screen bg-[#0a0e27] print:bg-white">
        {/* Print button - hidden when printing */}
        <div className="no-print p-6 flex gap-4 max-w-2xl mx-auto">
          <button
            onClick={() => window.print()}
            className="px-6 py-2 bg-gradient-to-r from-[#0ea5e9] to-[#06b6d4] text-white rounded-lg font-semibold hover:opacity-90 transition-colors"
          >
            Print / Save as PDF
          </button>
          <button
            onClick={() => window.close()}
            className="px-6 py-2 bg-[#334155] text-white rounded-lg font-semibold hover:bg-[#475569] transition-colors"
          >
            Close
          </button>
        </div>

        {/* Print content */}
        <div className="print-content max-w-2xl mx-auto p-8 bg-white text-[#1e293b] font-sans leading-relaxed">
          {isInternal && (
            <div className="mb-4 bg-red-100 border border-red-300 text-red-700 px-4 py-2 rounded font-bold text-sm print:bg-red-100">
              INTERNAL USE ONLY
            </div>
          )}

          <div className="text-2xl font-bold mb-6 text-[#0f172a]">#CONFIRMATION</div>

          <div className="mb-6 text-[#334155]">
            <div className="text-lg font-medium text-[#0f172a]">{formatDate(eventDetails.date)}</div>
            <div className="text-lg">{formatTime(eventDetails.startTime)} - {formatTime(eventDetails.endTime)}</div>
            <div>Address: {eventDetails.address}</div>
          </div>

          <div className="mb-6">
            <div className="font-bold mb-2 text-[#0f172a]">Performers:</div>
            {performers.map(p => (
              <div key={p.id} className="text-[#334155]">{p.name || 'Unnamed'} ${calculatePerformerCash(p)} cash</div>
            ))}
          </div>

          <div className="mb-6 text-[#334155]">
            <div>
              - Service {performerCount} performers
              {sameHours && ` ${globalHours} hours`} {eventDetails.serviceDescription}
              {sameRate && sameHours && ` ${globalRate}ph`}
            </div>
            {(!sameHours || !sameRate) && (
              <div className="ml-4 text-[#64748b] mt-1">
                {performers.map(p => (
                  <div key={p.id}>
                    {p.name || 'Unnamed'}: {getPerformerHours(p)}h @ ${getPerformerRate(p)}ph
                  </div>
                ))}
              </div>
            )}
            {sameTravel ? (
              <div className="mt-1">Plus individual travel ${globalTravel} each performer</div>
            ) : (
              performers.map(p => (
                <div key={p.id}>{p.name || 'Unnamed'} travel: ${p.travel}</div>
              ))
            )}
          </div>

          <div className="mb-6 text-[#334155]">
            <div>- Payment: Please prepare cash on arrival</div>
            <div>Transfer to {eventDetails.payId} pay ID ({eventDetails.payIdName})</div>
          </div>

          <div className="bg-[#f0fdf4] border border-[#86efac] p-4 rounded-lg mb-6 print:bg-[#f0fdf4]">
            <div className="text-lg font-bold text-[#0f172a]">TOTAL:</div>
            <div className="text-2xl font-bold text-[#16a34a]">${grandTotal}</div>
            <div className="mt-2 font-bold text-[#0f172a]">DEPOSIT:</div>
            <div className="text-xl font-bold text-[#0284c7]">${eventDetails.deposit}</div>
          </div>

          {addOns.filter(a => a.name && a.price > 0).length > 0 && (
            <div className="mb-6">
              <div className="font-bold mb-2 text-[#0f172a]">Any extensions per performer:</div>
              {addOns.filter(a => a.name && a.price > 0).map(a => (
                <div key={a.id} className="text-[#334155]">
                  ${a.price} {a.name}
                  {isInternal && (
                    <span className="text-[#64748b] text-sm ml-2">
                      (Performer: ${a.price - a.fee} | Company Fee: ${a.fee})
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}

          {isInternal && addOns.filter(a => a.name && a.price > 0).length > 0 && (
            <div className="bg-[#eff6ff] border-l-4 border-[#3b82f6] p-4 rounded-lg mb-6 print:bg-[#eff6ff]">
              <div className="font-bold text-[#1d4ed8] mb-1">Internal Summary</div>
              <div className="text-[#3b82f6] text-sm">
                Total potential add-on fees: ${addOns.filter(a => a.name && a.price > 0).reduce((sum, a) => sum + a.fee, 0)} per extension booked
              </div>
            </div>
          )}

          <div className="bg-[#fef3c7] border-l-4 border-[#f59e0b] p-4 rounded-lg print:bg-[#fef3c7]">
            <div className="font-bold text-[#b45309] mb-2">
              IMPORTANT TERMS & CONDITIONS
            </div>
            <div className="whitespace-pre-wrap text-[#92400e] text-sm">
              {disclaimer}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default PrintableQuote;
